import AppDatePicker from '@nxsys/components/AppDatePicker';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppRadioGroup from '@nxsys/components/AppRadioGroup';
import { yesNoOptions } from '@nxsys/constants/AppConstants';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';
import { Form } from 'antd';
import { FormProps } from 'antd/lib';
import { Moment } from 'moment';
import React, { Ref, useImperativeHandle } from 'react';
import { useSelector } from 'react-redux';

type SettingFormProps = {
  formRef?: Ref<SettingFormRef>;
  onSubmit?: (values: SettingValues) => Promise<void>;
  defaultValues?: Partial<SettingValues>;
};

export type SettingFormRef = {
  submit: () => void;
  reset: () => void;
};

export type SettingValues = {
  isSendEmailSelfBill: boolean;
  isSendEmailDraftSelfBill: boolean;
  isSendEmailP45: boolean;
  isSendEmailP60: boolean;
  sendEmailP60At: Moment | null;
  showHPEntitlement: boolean;
  showHPEmployerCostsYTD: boolean;
  verifyPayslip: boolean;
};

const SettingForm: React.FC<SettingFormProps> = ({
  formRef,
  onSubmit,
  defaultValues = {},
}) => {
  const { messages } = useTypedIntl();
  const { isBureau, payrollCountry, user } = useSelector(
    (state: AppState) => state.appStore,
  );
  const isIreland = payrollCountry === IRELAND;
  // NX-4971 — the payslip-verifier setting is super-admin only.
  const isSuperAdmin = !!user?.isSuperAdmin;

  const [form] = Form.useForm<SettingValues>();
  const isSendEmailP60 = Form.useWatch('isSendEmailP60', form);

  useImperativeHandle(formRef, () => ({
    submit() {
      form.submit();
    },
    reset() {
      form.resetFields();
    },
  }));

  const onFinish: FormProps['onFinish'] = async (values) => {
    await onSubmit?.(values);
  };

  const onFinishFailed: FormProps['onFinishFailed'] = (errorInfo) => {
    console.log('Failed:', errorInfo);
  };

  return (
    <AppFormContainer
      form={form}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      layout="vertical"
      labelCol={{ span: 24 }}
      wrapperCol={{ span: 24 }}
      initialValues={defaultValues}
    >
      <div className="text-[14px] font-bold mb-[16px]">
        {messages['common.communicationSettings']}
      </div>
      <Form.Item
        label={
          !isBureau
            ? messages['common.emailSelfBillPayslip']
            : messages['common.emailPayslip']
        }
        name="isSendEmailSelfBill"
      >
        <AppRadioGroup options={yesNoOptions} />
      </Form.Item>
      <Form.Item
        label={
          !isBureau
            ? messages['common.emailDraftSelfBillPayslip']
            : messages['common.emailDraftPayslip']
        }
        name="isSendEmailDraftSelfBill"
      >
        <AppRadioGroup options={yesNoOptions} />
      </Form.Item>
      {!isIreland && (
        <>
          <Form.Item label={messages['common.emailP45']} name="isSendEmailP45">
            <AppRadioGroup options={yesNoOptions} />
          </Form.Item>
          <Form.Item label={messages['common.emailP60']} name="isSendEmailP60">
            <AppRadioGroup options={yesNoOptions} />
          </Form.Item>
          {isSendEmailP60 && (
            <Form.Item
              label={messages['common.emailP60Date']}
              name="sendEmailP60At"
            >
              <AppDatePicker
                placeholder={messages['form.selectDate']}
                format="DD/MM"
              />
            </Form.Item>
          )}
        </>
      )}
      <div className="text-[14px] font-bold mb-[16px]">
        {messages['common.payslipSettings']}
      </div>
      <Form.Item
        label={messages['common.showHolidayPayEntitlement']}
        name="showHPEntitlement"
      >
        <AppRadioGroup options={yesNoOptions} />
      </Form.Item>
      {/* NX-5228: only the UK umbrella self-bill templates read this flag; the
          Irish payslip templates never render holiday-pay employer costs. */}
      {!isBureau && !isIreland && (
        <Form.Item
          label={messages['common.showHolidayPayEmployerCostsYTD']}
          name="showHPEmployerCostsYTD"
        >
          <AppRadioGroup options={yesNoOptions} />
        </Form.Item>
      )}
      {isSuperAdmin && (
        <Form.Item
          label={messages['common.verifyPayslip']}
          name="verifyPayslip"
        >
          <AppRadioGroup options={yesNoOptions} />
        </Form.Item>
      )}
    </AppFormContainer>
  );
};

export default SettingForm;
